import React, { useMemo, useState } from 'react';
import { useFamilyGraph } from '@/contexts/FamilyGraphContext';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { MessageCircle, Send, Trash2 } from 'lucide-react';

interface Props {
  memberId: string;
  entryId: string;
  entryType: 'memory' | 'photo';
  /** Compact mode hides the heading and limits height. */
  compact?: boolean;
}

const CommentsThread: React.FC<Props> = ({ memberId, entryId, entryType, compact }) => {
  const { comments, addComment, deleteComment } = useFamilyGraph();
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(!compact);

  const list = useMemo(
    () =>
      comments
        .filter((c) => c.entryType === entryType && c.entryId === entryId)
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
    [comments, entryId, entryType]
  );

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const authorName =
        (user?.user_metadata as any)?.full_name ||
        (user?.email ? user.email.split('@')[0] : null);
      await addComment(memberId, entryType, entryId, text.trim(), authorName);
      setText('');
    } catch (e: any) {
      toast.error(e?.message || 'Could not add comment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 border-t border-slate-100 pt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600 hover:text-fuchsia-700 transition"
      >
        <MessageCircle className="w-3.5 h-3.5" />
        {list.length === 0
          ? 'Add a detail'
          : `${list.length} ${list.length === 1 ? 'comment' : 'comments'}`}
        <span className="text-slate-400">{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div className="mt-2 space-y-2">
          {list.length > 0 && (
            <div className={`space-y-1.5 ${compact ? 'max-h-40 overflow-y-auto pr-1' : ''}`}>
              {list.map((c) => {
                const mine = c.authorUserId === user?.id;
                return (
                  <div
                    key={c.id}
                    className="rounded-md bg-slate-50 border border-slate-200 px-2.5 py-1.5 text-xs"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-slate-700 truncate">
                        {c.authorName || (mine ? 'You' : 'Family member')}
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-[10px] text-slate-400">
                          {new Date(c.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </span>
                        {mine && (
                          <button
                            type="button"
                            onClick={async () => {
                              if (!confirm('Delete this comment?')) return;
                              try {
                                await deleteComment(c.id);
                              } catch (e: any) {
                                toast.error(e?.message || 'Could not delete');
                              }
                            }}
                            aria-label="Delete comment"
                            className="text-rose-500 hover:text-rose-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-slate-800 whitespace-pre-wrap mt-0.5">{c.text}</div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex gap-1.5">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add a missing detail or comment…"
              className="text-xs h-9"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              onClick={submit}
              disabled={busy || !text.trim()}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white h-9 px-2.5"
            >
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentsThread;
